import {type FC, useEffect} from 'react';
import type {BusArrivalInfo} from './types';
import BusCard from './component/BusCard.tsx';
import {useQuery} from '@tanstack/react-query';
import callApi from '../../apis/callApi.ts';
import useCenterStore from '../../store/useCenterStore.ts';
import {FocusContext, setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import {useNavigate} from 'react-router';
import {BackButton, BusCardsRow, Container} from './types/BusInfo.styled.ts';

const BusInfoPage: FC = () => {
    const navigate = useNavigate();
    const {centerInfo} = useCenterStore();

    const {
        ref: busPageRef,
        focusKey: busInfoPageFocusKey,
        focusSelf: focusBusInfoPage,
    } = useFocusable({
        focusKey: 'bus-page',
        trackChildren: true,
    });

    const {ref: backBtnRef, focused: backBtnFocused} = useFocusable({
        focusKey: 'bus-back-button',
        onEnterPress: () => navigate(-1),
        onArrowPress: (direction) => {
            if (direction === 'down') {
                setFocus('bus-card-left');
                return false;
            }
            return true;
        },
    });

    useEffect(() => {
        setFocus('bus-back-button');
    }, []);

    // useEffect(() => {
    //     const timer = setTimeout(() => {
    //         focusBusInfoPage();
    //         requestAnimationFrame(() => {
    //             setFocus("bus-card-left");
    //         })
    //     }, 100);
    //     return () => {
    //         clearTimeout(timer);
    //     }
    // }, []);

    const {
        data: busesInfo,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['busInfo', '인천시청후문'],
        // queryFn: () => callApi().get(`/tvs/bus-arrival/?center_id=${centerInfo?.center_id ?? "DEV-000"}`),
        queryFn: () => callApi().get(`/utility/tvs/bus-arrival/?centerid=${centerInfo?.center_id ?? 'DEV-000'}`),
        refetchInterval: 1000 * 30,
        refetchOnMount: 'always',
        select: (data) => data.data,
    });

    if (isLoading || !busesInfo) {
        return (
            <Container>
                <div>버스 정보를 불러오는 중...</div>
            </Container>
        );
    }
    const station1Items = busesInfo?.station_1_info?.bus_info?.ServiceResult?.msgBody?.itemList ?? [];
    const station2Items = busesInfo?.station_2_info?.bus_info?.ServiceResult?.msgBody?.itemList ?? [];
    if (!station1Items || !station2Items) {
        return (
            <Container>
                <div>버스 정보를 가져올 수 없습니다.</div>
            </Container>
        );
    }

    const sortByArrivalTime = (a: BusArrivalInfo, b: BusArrivalInfo) => {
        return parseInt(a.ARRIVALESTIMATETIME) - parseInt(b.ARRIVALESTIMATETIME);
    };

    const station1Sorted = [...station1Items].sort(sortByArrivalTime);
    const station2Sorted = [...station2Items].sort(sortByArrivalTime);
    const arrivalSoonBusL = station1Sorted
        .filter((bus: BusArrivalInfo) => +bus.ARRIVALESTIMATETIME <= 60)
        .map((bus: BusArrivalInfo) => bus.ROUTENO);
    const arrivalSoonBusR = station2Sorted
        .filter((bus: BusArrivalInfo) => +bus.ARRIVALESTIMATETIME <= 60)
        .map((bus: BusArrivalInfo) => bus.ROUTENO);

    return (
        <FocusContext.Provider value={busInfoPageFocusKey}>
            <Container ref={busPageRef}>
                <BackButton ref={backBtnRef} $focused={backBtnFocused} onClick={() => navigate(-1)}>
                    &lt; 뒤로 가기
                </BackButton>
                <BusCardsRow>
                    <BusCard
                        direction="left"
                        stationName={busesInfo.station_1_info.name + ' (상행)'}
                        highlightBusNumber={arrivalSoonBusL}
                        buses={station1Sorted}
                    />
                    <BusCard
                        direction="right"
                        stationName={busesInfo.station_2_info.name + ' (하행)'}
                        highlightBusNumber={arrivalSoonBusR}
                        buses={station2Sorted}
                    />
                </BusCardsRow>
            </Container>
        </FocusContext.Provider>
    );
};

export default BusInfoPage;
